import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  getMeeting, openMeetingSession, respondToMeeting, cancelMeeting, notReady, reasonOf, whenOf, lengthOf, timeOf,
  canJoin, stateWords, recurrenceText, joinUrlOf, kindWords
} from '../../lib/meetings';
import { extractTodos, createTodoList, reasonOf as recReason, spokenLength, transcriptWords } from '../../lib/recordings';
import useRecordingReason from '../recording/useRecordingReason';
import { longDay } from '../../lib/quickAdd';
import { remembered, remember, resolve, videoConstraints, isGone } from '../../lib/devices';
import { relative } from '../../lib/when';
import { useMe } from '../../lib/me';
import { useFeature } from '../../lib/features';
import { contractorSignedIn } from '../../lib/contractorAuth';
import { DevicePanel } from '../CallButton';
import { Dialog, Faces, Avatar, GrowingTextarea } from '../todos/TodoPeople';
import { Icon } from '../todos/icons';
import { KindIcon, StateChip as RecStateChip, whenOf as recWhen } from '../recording/RecordingsPage';
import MinutesPanel, { MinutesChip } from './MinutesPanel';
import TodosModeChoice, { MODE_WORDS } from './TodosMode';
import MeetingForm from './MeetingForm';
import { StateChip, NotReadyPanel, RESPONSES, isOrganiser } from './MeetingsPage';
import '../recording/Recordings.css';
import './Meetings.css';

/*
 * ONE MEETING, at /meetings/:id, for somebody signed in (Gordon, 22 October).
 *
 * The top is the invitation as a page: title, when (Malta, 24-hour), how
 * long, where, who organised it, the agenda folded under a line, and the
 * attendees with their answers. Below it, until the meeting is over, the
 * PRE-JOIN CARD: your camera as a preview, the microphone / speakers /
 * camera panel from the call menu, and two buttons - Join with camera on,
 * Join with camera off. Pressing either asks the server for the meeting's
 * call (POST /meetings/:id/session) and opens SessionPage on it, in meeting
 * mode, with ?from= pointing back here, so leaving the call lands on the
 * meeting rather than in a dead end.
 *
 * AFTERWARDS the same page is where the meeting lives on: three tabs -
 * Recording (the recordings of it, each opening the player), Minutes
 * (MinutesPanel: written, reviewed, approved, sent) and To-dos (picked out
 * of the transcript, into one shared list or a private list each, the
 * organiser's choice preselected). The tabs are there as soon as there is a
 * recording, because a meeting that was recorded has something to show
 * before it is formally over.
 */

const RESPONSE_WORD = { accepted: 'Going', tentative: 'Maybe', declined: 'Not going', none: 'No answer yet' };
const RESPONSE_TONE = { accepted: 'good', tentative: 'warn', declined: 'bad' };

/** The server's proposal, as the editable draft the to-dos panel works on. */
const draftFrom = (summary, attendees) => {
  const people = Array.isArray(summary?.json?.people) ? summary.json.people : [];
  return {
    sid: summary?.id || null,
    people: people.map((p, i) => ({
      id: `${i}`,
      name: p.name || 'Unassigned',
      key: p.key || (attendees.find((a) => a.name === p.name)?.key) || '',
      items: (Array.isArray(p.items) ? p.items : []).map((it, j) => ({
        id: `${i}-${j}`, on: true, title: it.title || '', due_hint: it.due_hint || '', notes: it.notes || '',
        // The day the server worked out from what was said and the call's own day.
        due_date: it.due_date || null, due_time: it.due_time || null
      }))
    }))
  };
};

/* ------------------------------------------------------------- pre-join */

function PreJoin({ meeting, onJoin, busy, error }) {
  const [devices, setDevices] = useState(remembered);
  const [cameraOn, setCameraOn] = useState(false);
  const [camNote, setCamNote] = useState(null);
  const [camRefused, setCamRefused] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = useCallback(() => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  /** The preview, on the chosen camera, while it is wanted. */
  useEffect(() => {
    if (!cameraOn) { stopCamera(); return undefined; }
    let stopped = false;
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) { setCamRefused(true); setCameraOn(false); return; }
      const want = resolve('videoinput', null, devices);
      const open = (id) => navigator.mediaDevices.getUserMedia({ video: videoConstraints(id), audio: false });
      try {
        let stream;
        try {
          stream = await open(want.deviceId);
          setCamNote(null);
        } catch (err) {
          if (!want.deviceId || !isGone(err)) throw err;
          setCamNote(`${devices.videoinput?.label ? `"${devices.videoinput.label}"` : 'The remembered camera'} is not plugged in — using the system default.`);
          stream = await open(null);
        }
        if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
        stopCamera();
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setCamRefused(false);
      } catch (err) {
        if (stopped) return;
        if (err?.name === 'NotAllowedError') setCamRefused(true);
        setCameraOn(false);
      }
    })();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraOn, devices.videoinput?.id]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const choose = (kind, device) => setDevices(remember(kind, device));

  const join = (video) => {
    stopCamera();
    onJoin(video);
  };

  const live = meeting.state === 'live';
  return (
    <section className="mt-prejoin" aria-label="Join the meeting" data-camera={cameraOn ? 'on' : 'off'}>
      <div className="mt-preview">
        <video ref={videoRef} className="mt-preview-video" autoPlay playsInline muted data-live={cameraOn || undefined} />
        {!cameraOn && (
          <div className="mt-preview-off">
            <Icon name="person" size={34} />
            <span>{camRefused ? 'Your camera is blocked in this browser' : 'Your camera is off'}</span>
          </div>
        )}
        <div className="mt-preview-bar">
          <button type="button" className="se-flat mt-preview-toggle" aria-pressed={cameraOn} onClick={() => setCameraOn((v) => !v)}>
            {cameraOn ? 'Camera off' : 'Camera on'}
          </button>
          {camNote && <span className="mt-preview-note">{camNote}</span>}
        </div>
      </div>
      <div className="mt-prejoin-side">
        <h2 className="mt-h2">{live ? 'The meeting is on' : 'Ready to join?'}</h2>
        <p className="mt-quiet">
          Choose your microphone, speakers and camera first. They are remembered for your next call.
        </p>
        <DevicePanel choice={devices} onChoose={choose} idPrefix="mt-dev" />
        {error && <p className="mt-error" role="alert">{error}</p>}
        <div className="mt-prejoin-btns">
          <button type="button" className="btn btn-primary tds-btn mt-join-on" disabled={busy || camRefused} onClick={() => join(true)}>
            {busy ? 'Joining…' : 'Join with camera on'}
          </button>
          <button type="button" className="btn btn-outline tds-btn mt-join-off" disabled={busy} onClick={() => join(false)}>
            {busy ? 'Joining…' : 'Join with camera off'}
          </button>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- to-dos */

function MeetingTodos({ meeting, recordings, me }) {
  // With the AI off (078) nothing new is picked out of the transcript; a
  // list extracted before is still there to be made into to-dos.
  const aiOn = useFeature('ai');
  const ready = useMemo(() => (recordings || []).filter((r) => /Ready/.test(transcriptWords(r)))
    .sort((a, b) => (Date.parse(b.started_at || 0) || 0) - (Date.parse(a.started_at || 0) || 0)), [recordings]);
  const source = ready[0] || null;
  const attendees = useMemo(() => {
    const fromRec = Array.isArray(source?.attendees) ? source.attendees : [];
    const fromMeeting = (meeting.attendees || []).filter((a) => a.key).map((a) => ({ key: a.key, name: a.name }));
    const seen = new Set();
    return [...fromRec, ...fromMeeting].filter((a) => a.key && !seen.has(a.key) && seen.add(a.key));
  }, [source, meeting.attendees]);
  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState(meeting.todos_mode === 'personal' ? 'personal' : 'shared');
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(() => {
    const todos = (source?.summaries || []).filter((s) => s.kind === 'todos' && s.json);
    return todos.length ? draftFrom(todos[todos.length - 1], []) : null;
  });
  const [error, setError] = useState(null);
  const [made, setMade] = useState(null);
  const todoPath = me?.signedIn ? '/todos' : '/todo';
  // Not on the call: the reason first, once a day (3 Nov).
  const [withReason, reasonDialog] = useRecordingReason();

  const extract = async () => {
    if (!source) return;
    setBusy(true);
    setError(null);
    try {
      const s = await withReason(source.id, () => extractTodos(source.id, prompt.trim()), 'todos');
      setDraft(draftFrom(s, attendees));
    } catch (err) {
      setError(recReason(err, 'Could not pick the to-dos out.'));
    } finally {
      setBusy(false);
    }
  };

  const patchItem = (pid, iid, patch) => setDraft((d) => ({ ...d, people: d.people.map((p) => (p.id !== pid ? p
    : { ...p, items: p.items.map((it) => (it.id !== iid ? it : { ...it, ...patch })) })) }));
  const assign = (pid, key) => setDraft((d) => ({ ...d, people: d.people.map((p) => (p.id !== pid ? p
    : { ...p, key, name: key ? (attendees.find((a) => a.key === key)?.name || p.name) : 'Unassigned' })) }));

  const selection = useMemo(() => (draft?.people || []).flatMap((p) => p.items.filter((it) => it.on && it.title.trim())
    .map((it) => ({ key: p.key || null, name: p.key ? p.name : null, title: it.title.trim(),
      due_hint: it.due_hint || null, notes: it.notes || null, due_date: it.due_date || null, due_time: it.due_time || null }))), [draft]);

  const create = async () => {
    if (!draft?.sid || !selection.length) return;
    setCreating(true);
    setError(null);
    try {
      const r = await withReason(source.id, () => createTodoList(source.id, draft.sid, selection, mode), 'todos');
      setMade({ ...r, mode: r?.mode || mode, n: r?.created ?? selection.length,
        perPerson: Array.isArray(r?.per_person) ? r.per_person : r?.per_person && typeof r.per_person === 'object'
          ? Object.entries(r.per_person).map(([key, created]) => ({ key, created, name: attendees.find((a) => a.key === key)?.name || key })) : [],
        skipped: (Array.isArray(r?.skipped) ? r.skipped : []).map((x) => x.name).filter(Boolean) });
    } catch (err) {
      setError(recReason(err, 'Could not create the to-dos.'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <section className="rc-panel mt-todos" aria-label="To-dos" data-panel="todos">
      {reasonDialog}
      {error && <p className="mt-error" role="alert">{error}</p>}
      {!aiOn ? (
        <p className="rc-ai-off" role="note">
          AI summaries are switched off by your administrator, so to-dos are not picked out of the
          transcript. The transcript is still on the recording.
        </p>
      ) : (
      <form className="rc-ask" onSubmit={(e) => { e.preventDefault(); extract(); }}>
        <label className="tds-field">
          <span>Instruction (optional)</span>
          <GrowingTextarea rows={2} maxLength={1000} value={prompt} aria-label="Instruction for the to-dos"
            placeholder="e.g. only what was agreed for this week"
            onChange={(e) => setPrompt(e.target.value)} disabled={!source || busy} />
        </label>
        <div className="mt-ask-row">
          <button type="submit" className="btn btn-primary tds-btn rc-make mt-extract" disabled={!source || busy}>
            {busy ? 'Reading…' : draft ? 'Extract again' : 'Extract to-dos'}
          </button>
          {!source && (
            <span className="mt-quiet">
              {(recordings || []).length ? 'To-dos come from the transcript, which is not ready yet.'
                : 'To-dos are picked out of a recording of the meeting, and there is none yet.'}
            </span>
          )}
        </div>
      </form>
      )}

      {draft && (
        <div className="rc-draft">
          <p className="rc-quiet">
            What the meeting asked of each person. Untick what is wrong, edit the words, move an item to
            somebody else, then choose how the lists are made.
          </p>
          {draft.people.length === 0 && <p className="rc-quiet">Nothing in the transcript reads as a to-do.</p>}
          {draft.people.map((p) => (
            <div key={p.id} className="rc-person" data-person={p.key || 'unassigned'}>
              <div className="rc-person-head">
                <Avatar name={p.key ? p.name : '?'} who={p.key || 'unassigned'} />
                <select className="rc-assign" value={p.key || ''} aria-label="Who these are for" onChange={(e) => assign(p.id, e.target.value)}>
                  <option value="">Unassigned</option>
                  {attendees.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
                  {p.key && !attendees.some((a) => a.key === p.key) && <option value={p.key}>{p.name}</option>}
                </select>
                <span className="rc-person-n">{p.items.filter((it) => it.on).length} of {p.items.length}</span>
              </div>
              <ul className="rc-items">
                {p.items.map((it) => (
                  <li key={it.id} className={`rc-item${it.on ? '' : ' is-off'}`}>
                    <input type="checkbox" className="rc-item-check" checked={it.on} aria-label={`Include "${it.title}"`}
                      onChange={(e) => patchItem(p.id, it.id, { on: e.target.checked })} />
                    <span className="rc-item-main">
                      <input className="rc-item-title" value={it.title} aria-label="To-do"
                        onChange={(e) => patchItem(p.id, it.id, { title: e.target.value })} />
                      {(it.due_hint || it.notes) && (
                        <span className="rc-item-meta">
                          {it.due_hint && <span className="rc-chip is-quiet" data-due={it.due_date || ''}><Icon name="calendar" size={12} /> {it.due_hint}
                            {it.due_date ? ` · ${longDay(it.due_date)}${it.due_time ? ` ${it.due_time}` : ''}` : ''}</span>}
                          {it.notes && <span className="rc-item-notes">{it.notes}</span>}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {draft.people.length > 0 && (
            <div className="mt-todos-foot">
              <TodosModeChoice value={mode} onChange={setMode} name="mt-todos-mode" />
              <div className="rc-draft-foot">
                <span className="rc-quiet">{selection.length} to-do{selection.length === 1 ? '' : 's'} ticked</span>
                <button type="button" className="btn btn-primary tds-btn rc-create mt-create-todos" disabled={creating || !selection.length} onClick={create}>
                  {creating ? 'Creating…' : mode === 'personal' ? 'Create a list each' : 'Create shared list'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {made && (
        <div className="tds-toast rc-toast mt-toast" role="status">
          <span>
            {made.mode === 'personal'
              ? <>Created in each person&rsquo;s own Inbox · {made.n} to-do{made.n === 1 ? '' : 's'}
                {made.perPerson.length > 0 && (
                  <span className="rc-toast-skipped mt-per-person">
                    {made.perPerson.map((p) => `${p.name || p.key}: ${p.created}`).join(' · ')}
                  </span>
                )}</>
              : `Shared list${made.name ? ` “${made.name}”` : ''} created · ${made.n} to-do${made.n === 1 ? '' : 's'}`}
            {made.skipped?.length > 0 && (
              <span className="rc-toast-skipped">
                {made.skipped.join(', ')} could not be given theirs; those items were created unassigned.
              </span>
            )}
          </span>
          {made.list_id && (
            <Link className="tds-link" to={`${todoPath}?list=${encodeURIComponent(made.list_id)}`} onClick={() => setMade(null)}>Open the list</Link>
          )}
          <button type="button" className="tds-x" aria-label="Dismiss" onClick={() => setMade(null)}><Icon name="close" size={16} /></button>
        </div>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------- page */

export default function MeetingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const me = useMe();
  // 078, Settings -> Features: Record in the room needs recording; the
  // Recording tab opens recordings, and the To-dos tab makes to-dos - each
  // is left out while its feature is off for this person.
  const recordingOn = useFeature('recording');
  const todosOn = useFeature('todos');
  const [params, setParams] = useSearchParams();
  const [m, setM] = useState(null);
  const [error, setError] = useState(null);
  const [notThere, setNotThere] = useState(false);
  const [gone, setGone] = useState(false);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [scope, setScope] = useState('this');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => new Date());
  // ?minutes=<id> (the bell's minutes_ready) opens that version, on its tab.
  const focusMinutes = params.get('minutes') || null;
  const tab = ['recording', 'minutes', 'todos'].includes(params.get('tab')) ? params.get('tab') : focusMinutes ? 'minutes' : null;

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    try {
      const got = await getMeeting(id);
      setM(got);
      setError(null);
    } catch (err) {
      if (notReady(err)) { setNotThere(true); return; }
      if (err.response?.status === 404) { setGone(true); return; }
      setError(reasonOf(err, 'Could not open that meeting.'));
    }
  }, [id]);

  useEffect(() => { setM(null); load(); }, [load]);

  // A live meeting is looked at again every half minute, so the state chip
  // and the recordings list follow it without a reload.
  useEffect(() => {
    if (!m || (m.state !== 'live' && !canJoin(m, now))) return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [m, now, load]);

  const setTab = (t) => setParams((p) => { const next = new URLSearchParams(p); next.set('tab', t); return next; }, { replace: true });
  const setMinutes = (fn) => setM((cur) => (cur ? { ...cur, minutes: typeof fn === 'function' ? fn(cur.minutes || []) : fn } : cur));

  const join = async (video) => {
    setJoining(true);
    setJoinError(null);
    try {
      const s = await openMeetingSession(id);
      navigate(`/sessions/${s.session_id || s.id}?from=${encodeURIComponent(`/meetings/${id}`)}&video=${video ? 1 : 0}`);
    } catch (err) {
      const d = err.response?.data || {};
      setJoinError(d.cancelled || err.response?.status === 410 ? 'This meeting was cancelled.'
        : d.ended ? 'This meeting has ended.'
          : reasonOf(err, 'Could not open the meeting\'s call.'));
      setJoining(false);
      if (d.cancelled || d.ended || err.response?.status === 410) load();
    }
  };

  const respond = async (response) => {
    const before = m;
    setM((cur) => ({ ...cur, my_response: response }));
    try { await respondToMeeting(id, response); } catch (err) { setM(before); setError(reasonOf(err, 'Could not send your answer.')); }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      await cancelMeeting(id, m.series_id ? scope : 'this');
      navigate('/meetings', { replace: true });
    } catch (err) {
      setError(reasonOf(err, 'Could not cancel the meeting.'));
      setCancelling(false);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(joinUrlOf(m)); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* refused */ }
  };
  // A guest's own link, which the server hands only to the organiser or an
  // administrator - so the button is there exactly when there is one.
  const [copiedGuest, setCopiedGuest] = useState(null);
  const copyGuest = async (a) => {
    try { await navigator.clipboard.writeText(a.join_url); setCopiedGuest(a.id || a.email); setTimeout(() => setCopiedGuest(null), 1800); } catch { /* refused */ }
  };

  if (notThere) return <div className="mt-page"><NotReadyPanel /></div>;
  if (gone) {
    return (
      <div className="mt-page">
        <p className="mt-error" role="alert">That meeting is not there - it may have been cancelled.</p>
        <Link className="mt-back" to="/meetings">Back to meetings</Link>
      </div>
    );
  }
  if (!m && error) {
    return (
      <div className="mt-page">
        <p className="mt-error" role="alert">{error}</p>
        <Link className="mt-back" to="/meetings">Back to meetings</Link>
      </div>
    );
  }
  if (!m) return <div className="mt-page"><p className="mt-quiet">Loading…</p></div>;

  const words = stateWords(m, now);
  const over = words === 'Ended' || words === 'Cancelled';
  const mine = isOrganiser(m, me);
  const attendees = Array.isArray(m.attendees) ? m.attendees : [];
  const recordings = Array.isArray(m.recordings) ? m.recordings : [];
  const minutes = Array.isArray(m.minutes) ? m.minutes : [];
  const after = over || recordings.length > 0 || minutes.length > 0;
  const tabs = [['recording', 'Recording', recordings.length], ['minutes', 'Minutes', minutes.length], ['todos', 'To-dos', 0]]
    .filter(([k]) => (k === 'recording' ? recordingOn : k === 'todos' ? todosOn : true));
  const wanted = tab || (after ? (minutes.length || !recordingOn ? 'minutes' : 'recording') : null);
  const shownTab = wanted && tabs.some(([k]) => k === wanted) ? wanted : (after ? 'minutes' : null);
  const joinable = canJoin(m, now);
  const latestMinutes = minutes.slice().sort((a, b) => (Number(b.version) || 0) - (Number(a.version) || 0))[0] || null;
  const agendaLines = String(m.agenda || '').split(/\r?\n/).filter((l) => l.trim());

  return (
    <div className="mt-page mt-detail" data-state={words.toLowerCase()}>
      <nav className="mt-crumbs" aria-label="Where you are">
        <Link to="/meetings">Meetings</Link>
        <span className="mt-sep" aria-hidden="true">/</span>
        <span>{m.title || 'Meeting'}</span>
      </nav>

      <header className="mt-detail-head">
        <div className="mt-head-words">
          <h1 className="mt-title">{m.title || 'Meeting'}</h1>
          <p className="mt-sub mt-detail-when">
            <Icon name="calendar" size={14} />
            <time dateTime={m.starts_at || undefined}>{whenOf(m.starts_at, m.ends_at, now)}</time>
            <span className="mt-sep">·</span>{lengthOf(m.starts_at, m.ends_at)}
            <span className="mt-sep">·</span>Malta time
          </p>
          <div className="mt-chips">
            <StateChip meeting={m} now={now} />
            {m.recurrence && <span className="mt-chip is-quiet"><Icon name="repeat" size={12} /> {recurrenceText(m.recurrence, now)}</span>}
            <span className="mt-chip is-quiet" data-kind={m.kind}>
              <Icon name={m.kind === 'room' ? 'home' : m.kind === 'call' ? 'share' : 'link'} size={12} /> {kindWords(m)}
            </span>
            {m.adhoc && <span className="mt-chip is-quiet mt-adhoc-chip" title="Started on the spot, not planned">Ad hoc</span>}
            {m.organiser_name && <span className="mt-chip is-quiet"><Icon name="person" size={12} /> {mine ? 'Organised by you' : `Organised by ${m.organiser_name}`}</span>}
            {latestMinutes && <MinutesChip state={latestMinutes.state} />}
          </div>
        </div>
        <div className="mt-detail-actions">
          {m.kind === 'online' && !over && (
            <button type="button" className="se-flat mt-copy" onClick={copy}>{copied ? 'Copied' : 'Copy join link'}</button>
          )}
          {mine && (!over || m.adhoc) && (
            // An ad-hoc meeting's details are often filled in AFTER it ended -
            // the title, who was there - so Edit stays; Cancel does not (there
            // is nothing to withdraw from anybody's calendar).
            <>
              <button type="button" className="se-flat mt-edit" onClick={() => setEditing(true)}>Edit</button>
              {!over && <button type="button" className="se-flat is-danger mt-cancel" onClick={() => { setScope('this'); setCancelling(true); }}>Cancel</button>}
            </>
          )}
        </div>
      </header>

      {error && (
        <p className="mt-error" role="alert">
          {error} <button type="button" className="tds-link" onClick={() => setError(null)}>Dismiss</button>
        </p>
      )}

      <div className="mt-detail-body">
        <div className="mt-detail-main">
          {agendaLines.length > 0 && (
            <details className="mt-agenda" open={!after}>
              <summary><Icon name="list" size={14} /> Agenda</summary>
              <ol className="mt-agenda-list">
                {agendaLines.map((l, i) => <li key={i}>{l.replace(/^\s*(\d+[.)]|[-*•])\s*/, '')}</li>)}
              </ol>
            </details>
          )}

          {!mine && !over && (
            <div className="mt-answer" role="group" aria-label="Your answer">
              <span className="mt-answer-label">Your answer</span>
              <div className="mt-respond">
                {RESPONSES.map(([k, w]) => (
                  <button key={k} type="button" className={`mt-respond-btn is-${k}${m.my_response === k ? ' is-on' : ''}`}
                    aria-pressed={m.my_response === k} data-response={k} onClick={() => respond(k)}>{w}</button>
                ))}
              </div>
            </div>
          )}

          {m.kind === 'online' && !over && (
            joinable ? <PreJoin meeting={m} onJoin={join} busy={joining} error={joinError} /> : (
              <div className="mt-notyet">
                <Icon name="clock" size={18} />
                <span>
                  Join opens 15 minutes before the start, at <strong>{timeOf(new Date(new Date(m.starts_at).getTime() - 15 * 60000).toISOString())}</strong>.
                </span>
              </div>
            )
          )}
          {m.kind === 'room' && !over && (
            <div className="mt-notyet mt-room">
              <Icon name="home" size={18} />
              <span>This meeting is in the room{m.location ? ` — ${m.location}` : ''}. Record it from a phone or laptop on the table when it starts.</span>
              {/* The in-room recorder (RoomRecorder): one device on the table,
                  breaks and all, then the same transcript, minutes and to-dos.
                  Staff only: /meetings/:id/record and the POST behind it are
                  both staff-gated, so for a contractor this link landed on
                  the staff login page. */}
              {!contractorSignedIn() && recordingOn && (
                <Link to={`/meetings/${m.id}/record`} className="btn btn-primary tds-btn mt-record-room">
                  <Icon name="mic" size={15} /> {m.state === 'live' && m.adhoc ? 'Back to the recorder' : 'Record in the room'}
                </Link>
              )}
            </div>
          )}
          {m.kind === 'call' && !over && (
            <div className="mt-notyet mt-room mt-call">
              <Icon name="share" size={18} />
              <span>
                A call on {m.platform ? kindWords(m).replace(/^Call on /, '') : 'another app'}. It is recorded from the computer the call is on:
                your microphone and the call&rsquo;s sound, mixed, then the same transcript, minutes and to-dos.
              </span>
              {!contractorSignedIn() && recordingOn && (
                <Link to={`/meetings/${m.id}/record`} className="btn btn-primary tds-btn mt-record-room mt-record-call-link">
                  <Icon name="mic" size={15} /> {m.state === 'live' ? 'Back to the recorder' : 'Record the call'}
                </Link>
              )}
            </div>
          )}

          {after && (
            <section className="mt-after" aria-label="After the meeting">
              <div className="tds-tabs mt-tabs" role="tablist" aria-label="After the meeting">
                {tabs.map(([k, w, n]) => (
                  <button key={k} type="button" role="tab" className="tds-tab mt-tab" aria-selected={shownTab === k} data-tab={k} onClick={() => setTab(k)}>
                    {w}{n > 0 && <span className="tds-tab-n">{n}</span>}
                  </button>
                ))}
              </div>
              {shownTab === 'recording' && (
                <div className="mt-recordings" data-panel="recording">
                  {recordings.length === 0 ? (
                    <p className="mt-quiet">This meeting was not recorded.</p>
                  ) : (
                    <ul className="rc-list mt-rec-list">
                      {recordings.map((r) => (
                        <li key={r.id} className="rc-row">
                          <Link to={`/recordings/${r.id}`} className="rc-row-btn mt-rec-row">
                            <span className={`rc-row-kind is-${r.kind === 'video' ? 'video' : 'audio'}`}><KindIcon kind={r.kind} /></span>
                            <span className="rc-row-main">
                              <span className="rc-row-about"><span className="rc-row-label">{recWhen(r.started_at)}</span></span>
                              <span className="rc-row-meta">
                                {r.duration_ms > 0 ? spokenLength(r.duration_ms) : ''}
                                {r.started_by_name ? ` · started by ${r.started_by_name}` : ''}
                              </span>
                            </span>
                            <span className="rc-row-chips"><RecStateChip rec={r} /></span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {shownTab === 'minutes' && (
                <MinutesPanel meeting={m} recordings={recordings} minutes={minutes} onChange={setMinutes}
                  canEdit={mine || Boolean(me?.isAdmin)} focusId={focusMinutes} />
              )}
              {shownTab === 'todos' && <MeetingTodos meeting={m} recordings={recordings} me={me} />}
            </section>
          )}
        </div>

        <aside className="mt-detail-side">
          <h2 className="mt-h2">Who</h2>
          <ul className="mt-attendees" aria-label="Attendees">
            {m.organiser_name && (
              <li className="mt-attendee is-organiser" data-attendee={m.organiser_key || 'organiser'}>
                <Avatar name={m.organiser_name} who={m.organiser_key || m.organiser_name} />
                <span className="mt-attendee-name">{m.organiser_name}</span>
                <span className="mt-chip is-quiet">Organiser</span>
              </li>
            )}
            {attendees.filter((a) => a.role !== 'organiser' && a.key !== m.organiser_key).map((a) => (
              <li key={a.id || a.key || a.email} className={`mt-attendee${a.kind === 'guest' ? ' is-guest' : ''}`} data-attendee={a.key || a.email}>
                <Avatar name={a.name || a.email} who={a.key || a.email} />
                <span className="mt-attendee-name">
                  <span className="mt-attendee-main">
                    {a.name || a.email}
                    {a.kind === 'contractor' && <span className="tds-it" title="A contractor">IT</span>}
                  </span>
                  {a.kind === 'guest' && <small className="mt-attendee-sub" title={a.email || undefined}>{a.email ? `${a.email} · outside guest` : 'by name · not in Jarvis'}</small>}
                </span>
                {/* An ad-hoc meeting asked nobody: "No answer yet" would be a question nobody was asked. */}
                {!m.adhoc && (
                  <span className={`mt-chip is-${RESPONSE_TONE[a.response] || 'quiet'}`} data-response={a.response || 'none'}>
                    {RESPONSE_WORD[a.response] || RESPONSE_WORD.none}
                  </span>
                )}
                {a.kind === 'guest' && a.join_url && !over && (
                  <button type="button" className="tds-link mt-copy-guest" title="Copy this guest's own join link" onClick={() => copyGuest(a)}>
                    {copiedGuest === (a.id || a.email) ? 'Copied' : 'Copy guest link'}
                  </button>
                )}
              </li>
            ))}
            {attendees.filter((a) => a.role !== 'organiser' && a.key !== m.organiser_key).length === 0 && (
              <li className="mt-quiet">{m.adhoc ? 'Nobody else listed yet - Edit adds who was there.' : 'Nobody else has been invited yet.'}</li>
            )}
          </ul>
          <Faces members={attendees.filter((a) => a.key).map((a) => ({ key: a.key, name: a.name }))} max={6} />
          {!mine && m.minutes_mode && (
            <p className="mt-quiet mt-side-note">
              {m.minutes_mode === 'auto' ? 'Minutes are sent to everybody as soon as they are written.'
                : 'Minutes are reviewed by the organiser before they are shared.'}
              {' '}To-dos go into {MODE_WORDS[m.todos_mode === 'personal' ? 'personal' : 'shared'].label.toLowerCase()}.
            </p>
          )}
          {recordings.length > 0 && (
            <p className="mt-quiet mt-side-note">
              Recorded {relative(recordings[0].started_at)}.
            </p>
          )}
        </aside>
      </div>

      {editing && <MeetingForm meeting={m} onSaved={(saved) => { setEditing(false); setM((cur) => ({ ...cur, ...(saved || {}) })); load(); }} onClose={() => setEditing(false)} />}

      {cancelling && (
        <Dialog label={`Cancel “${m.title}”?`} onClose={() => setCancelling(false)} className="mt-scope-dialog">
          <p className="tds-quiet">Everybody invited is told, and the invitation is withdrawn from their calendars.</p>
          {m.series_id && (
            <div className="mt-scope" role="radiogroup" aria-label="Which meetings to cancel">
              {[['this', 'This meeting only'], ['following', 'This and the following ones'], ['all', 'All meetings in the series']].map(([k, w]) => (
                <label key={k} className={`mt-scope-opt${scope === k ? ' is-on' : ''}`}>
                  <input type="radio" name="mt-cancel-scope" value={k} checked={scope === k} onChange={() => setScope(k)} />
                  <span>{w}</span>
                </label>
              ))}
            </div>
          )}
          <div className="tds-dialog-foot">
            <button type="button" className="btn btn-outline tds-btn" disabled={busy} onClick={() => setCancelling(false)}>Keep it</button>
            <button type="button" className="btn btn-primary tds-btn mt-cancel-confirm" disabled={busy} onClick={doCancel}>
              {busy ? 'Cancelling…' : 'Cancel the meeting'}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
